import { useEffect, useState, useRef } from "react";
import debounce from "lodash/debounce";
import axios from "axios";
import { uploadMediaFile } from "../../utills/fileUpload";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/languageContext";
import { fetchAllBookCategoriesByLangOptions, fetchAllRecommendersByLangOptions, normalizeCoverImage, isBlankHtml } from "../../helper";
import { useConfirm } from "../../components/confirmDialog";


const useForm = (callback, validate, adminInfo) => {
  const { languages, selectedLang, setSelectedLang, initialLangRef } = useLanguage();
  const confirm = useConfirm();

  const inputRef = useRef();
  const generatingRef = useRef(false);
  // Generate overview awaits the panel's dialog, which belongs to the app and
  // outlives this form. `overviewBusyRef` is claimed before that wait so a
  // second trigger can't run alongside it; `mountedRef` stops a Replace
  // pressed after leaving the page from paying for an overview nobody sees.
  const overviewBusyRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const createInitialLangValues = (lang) => ({
    langCode: lang,
    bookName: "",
    authorName: [],
    relatedBooks: [],
    recommendedBy: [],
    genre: null,
    aboutTheBook: "",
    tagline: "",
    whyItMatters: "",
    reflectionQuestions: "",
    ideas: "",
    actionableInsights: "",
    shouldReadFor: "",
    curatedQuotes: "",
    chapters: [{ content: "", audio: null, chapterName: "" }],
    media: { audio: null, video: null },
  });

  const normalizeChapterContent = (content) => {
    if (typeof content !== "string") return content || "";
    const hasHtmlTags = /<[^>]+>/.test(content);
    if (hasHtmlTags) return content;
    return content.replace(/\\n/g, "\n");
  };

  const normalizeChapters = (chapters = []) =>
    (Array.isArray(chapters) ? chapters : []).map((chapter) => ({
      ...chapter,
      content: normalizeChapterContent(chapter?.content),
    }));

  const createInitialTranslationsArr = () => languages.map((lang) => createInitialLangValues(lang));

  const [values, setValues] = useState({
    bookSummaryId: "",
    coverImage: {},
    translations: createInitialTranslationsArr(),
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isSubmitSummary, setIsSubmitSummary] = useState(false);
  const [isGeneratingOverview, setIsGeneratingOverview] = useState(false);
  const [editMediaError, setEditMediaError] = useState("");

  const [booksData, setBooksData] = useState([]);
  // Matches the server left out of booksData for already having a summary —
  // shown, dimmed, so the search says so instead of "no books found".
  const [summarizedBooks, setSummarizedBooks] = useState([]);
  const [relatedBooksCount, setRelatedBooksCount] = useState(0);
  const [searchBookName, setSearchBookName] = useState("");
  const [recommendeBooks, setRecommendeBooks] = useState([]);
  const [recommenderNames, setRecommenderNames] = useState([]);
  const [bookSummaryId, setBookSummaryId] = useState("");

  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isExecutingRef = useRef(false);

  // Debounced. The picker fired a request per keystroke, and each one scans
  // 11,000 books — typing "atomic habits" meant thirteen queries, several
  // near a second, arriving out of order. 300 ms means one request per pause
  // in typing instead.
  useEffect(() => {
    if (!searchBookName) return undefined;
    const timer = setTimeout(() => fetchAllBooksCategoriesData(), 300);
    return () => clearTimeout(timer);
  }, [searchBookName]);

  useEffect(() => {
    fetchAllBooksData();
  }, [selectedLang]);

  const handleUploadAudioAndVideo = async (event) => {
    const result = await uploadMediaFile(event, true);

    if (typeof result === "string") {
      setEditMediaError(result);
    } else if (typeof result === "object" && result.length > 0) {
      const uploadedFile = result[0]?.data;

      setValues((prevValues) => ({
        ...prevValues,
        translations: prevValues.translations.map((t) => {
          if (t.langCode !== selectedLang) return t;

          const updatedMedia = { ...t.media };
          if (uploadedFile.mediaType.startsWith("audio")) {
            updatedMedia.audio = uploadedFile;
          } else if (uploadedFile.mediaType.startsWith("video")) {
            updatedMedia.video = uploadedFile;
          }

          return {
            ...t,
            media: updatedMedia,
          };
        }),
      }));
    }
  };

  const fetchAllBooksData = async () => {
    const bookNameOptions = await fetchAllBookCategoriesByLangOptions(selectedLang, adminInfo.token);
    const options = await fetchAllRecommendersByLangOptions(selectedLang, adminInfo.token);

    const bookOptions = bookNameOptions.map((book) => ({
      label: book.bookName,
      value: book.bookId,
    }));

    const recommendersNameOption = options.map((recommend) => ({
      label: recommend.recommenderName,
      value: recommend.recommenderId,
    }));

    setRecommendeBooks(bookOptions);
    setRecommenderNames(recommendersNameOption);
  };

  const handleUploadMedia = async (event) => {
    // uploadType "cover" triggers backend variant generation (thumb/detail/master).
    const result = await uploadMediaFile(event, true, "cover");

    if (typeof result === "string") {
      setEditMediaError(result);
    } else if (typeof result === "object" && result.length > 0) {
      setEditMediaError("");
      // Store the whole returned object (uploadedImageUrl/Key + thumb/detail/master),
      // with the S3 -> CloudFront swap applied to every URL.
      setValues((prevValues) => ({
        ...prevValues,
        coverImage: normalizeCoverImage(result[0]?.data),
      }));
    }
  };

  const handleRemoveCoverImage = () => {
    setValues((prevValues) => ({
      ...prevValues,
      coverImage: {},
    }));
  };

  const handleUploadAudioFileInChapter = async (event, chapterIndex) => {
    const result = await uploadMediaFile(event, true);

    if (typeof result === "string") {
      setEditMediaError(result);
    } else if (typeof result === "object" && result.length > 0) {
      const uploadedFile = result[0]?.data;

      setValues((prevValues) => ({
        ...prevValues,
        translations: prevValues.translations.map((t) => {
          if (t.langCode !== selectedLang) return t;

          const updatedChapters = t.chapters.map((chapter, index) =>
            index === chapterIndex ? { ...chapter, audio: uploadedFile } : chapter
          );

          return {
            ...t,
            chapters: updatedChapters,
          };
        }),
      }));
    }
  };

  const handleSearchTextChange = debounce((text) => {
    setSearchBookName(text);
  }, 500);

  const handleCreateAnIndexInChapters = () => {
    setValues((prevValues) => ({
      ...prevValues,
      translations: prevValues.translations.map((t) =>
        t.langCode === selectedLang
          ? {
            ...t,
            chapters: [...(t.chapters || []), { content: "", audio: null }],
          }
          : t
      ),
    }));
  };

  const handleAddValues = (field, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [field]: value,
    }));
  };

  const handleUpdateTranslationField = (field, value, language) => {
    setValues((prevValues) => ({
      ...prevValues,
      translations: prevValues.translations.map((lang) =>
        lang.langCode === language
          ? { ...lang, [field]: value }
          : lang
      ),
    }));
  };

  const handleGetParticularBookDetail = async (bookId) => {
    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/books/details/${bookId}`, { selectedLang }, { headers });

      if (response.status) {
        const translationData = response?.data;

        setValues((prevValues) => {
          const updatedTranslations = prevValues.translations.map((langData) =>
            langData.langCode === selectedLang
              ? {
                ...langData,
                bookName: translationData?.bookName || "",
                authorName: translationData?.authorName ? translationData?.authorName : [],
                genre: translationData?.genres ? translationData?.genres : [],
                aboutTheBook: translationData?.aboutBook || "",
                // Seeded from the book, then owned by the summary — the same
                // rule the cover follows. Saving never writes it back to the
                // book, so retitling the pitch here leaves the catalogue
                // entry (and every screen reading it) untouched.
                tagline: translationData?.tagline || "",
                relatedBooks: (translationData.similarBooks?.length > 0)
                  ? translationData.similarBooks
                  : [],
                recommendedBy: (translationData.recommendedByUsers?.length > 0)
                  ? translationData.recommendedByUsers
                  : [],
              }
              : langData
          );
          setRelatedBooksCount(translationData.relatedBooksCount);

          return {
            ...prevValues,
            bookSummaryId: bookId,
            translations: updatedTranslations,
          };
        });
        setBookSummaryId(bookId);
        setSearchBookName("");
      }
    } catch (error) {
      console.error("Failed to get book details:", error);
    }
  };

  const fetchAllBooksCategoriesData = async () => {
    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/books/getall/booknames/${encodeURIComponent(searchBookName)}`, { headers });

      if (response.status === true) {
        const normalizedBooks = response.data.map(book => ({
          ...book,
          coverImage: normalizeCoverImage(book.coverImage)
        }));
        setBooksData(normalizedBooks);
        setSummarizedBooks((response.summarized || []).map(book => ({
          ...book,
          coverImage: normalizeCoverImage(book.coverImage)
        })));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleAutoGenerateChapters = async (bookSummaryId) => {
    setIsSubmitSummary(true);
    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/summary/generate-summary-by-openai-model/${bookSummaryId}`, { headers });

      if (response.status === true) {
        setIsSubmitSummary(false);
        setValues((prevValues) => ({
          ...prevValues,
          translations: prevValues.translations.map((t) =>
            t.langCode === selectedLang
              ? { ...t, chapters: normalizeChapters(response.data) }
              : t
          ),
        }));
        toast.success("Chapters generated successfully!");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsSubmitSummary(false);
    }
  };

  // The Overview tab's four fields, from the "Overview generation" task in AI
  // Settings. Written into the ENGLISH translation — the source the other
  // languages are translated from on save — and nothing is saved until the
  // editor presses Save, so a poor generation costs nothing but a re-run.
  // Notable Quotes (`curatedQuotes`) is not generated any more — it is hidden
  // (hiddenSections.js), and whatever a summary already stores is left alone.
  const OVERVIEW_FIELDS = ["aboutTheBook", "shouldReadFor", "actionableInsights", "reflectionQuestions"];

  const handleGenerateOverview = async (bookId) => {
    if (!bookId) {
      toast.error("Pick the book first.");
      return;
    }
    if (overviewBusyRef.current) return;
    overviewBusyRef.current = true;
    const english = values.translations?.find((t) => t.langCode === "en") || {};
    const hasText = OVERVIEW_FIELDS.some((field) => isBlankHtml(english[field] || "") === false);
    // The panel's dialog, not window.confirm: once Chrome is told to block a
    // site's dialogs, window.confirm returns false without showing anything,
    // and the button silently did nothing on any summary with an overview.
    // Add and Edit share this hook, and their save buttons are labelled
    // differently — so the copy names neither.
    if (
      hasText &&
      !(await confirm({
        title: "Replace the Overview?",
        body: "The Overview fields already filled in will be replaced with a newly generated overview. Nothing is saved until you save the summary.",
        confirmLabel: "Replace",
        tone: "primary",
      }))
    ) {
      overviewBusyRef.current = false;
      return;
    }
    if (!mountedRef.current) return;

    setIsGeneratingOverview(true);
    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/api/admin/summary/generate-overview/${bookId}`,
        { headers }
      );
      if (response.status === true) {
        const fields = response.data?.fields || {};
        setValues((prevValues) => ({
          ...prevValues,
          translations: prevValues.translations.map((t) =>
            t.langCode === "en"
              ? {
                  ...t,
                  // A section the model left out keeps what was there.
                  ...Object.fromEntries(OVERVIEW_FIELDS.filter((f) => fields[f]).map((f) => [f, fields[f]])),
                }
              : t
          ),
        }));
        const found = response.data?.sectionsParsed ?? 0;
        const expected = response.data?.sectionsExpected ?? OVERVIEW_FIELDS.length;
        if (found < expected) {
          toast.warn(`Overview generated, but only ${found} of ${expected} sections came back — review before saving.`);
        } else {
          toast.success("Overview generated — review it before saving.");
        }
      }
    } catch (error) {
      console.error("Error generating overview:", error);
      toast.error(error?.response?.data?.error || "Could not generate the overview.");
    } finally {
      overviewBusyRef.current = false;
      if (mountedRef.current) setIsGeneratingOverview(false);
    }
  };

  const handleRemoveAnIndexInChapters = (selectedIndex) => {
    // Guard on how many chapters are left, not on the index. The old check was
    // `selectedIndex > 0`, which made chapter 1 permanently undeletable — the
    // intent was surely "never leave a summary with zero chapters".
    const currentChapters =
      values.translations?.find((t) => t.langCode === selectedLang)?.chapters ?? [];
    if (currentChapters.length > 1) {
      setValues((prevValues) => ({
        ...prevValues,
        // Delete the chapter from EVERY language, not just the one on screen.
        // Chapters are parallel arrays built from the English source, so it is
        // position — not _id — that links a chapter to its translations (each
        // translation mints its own _id). Dropping it from one language alone
        // leaves the others with an orphan chapter.
        translations: prevValues.translations.map((t) => {
          const chapters = t.chapters ?? [];
          // Skip a language with no chapter at this index, and one that would be
          // emptied: a translation still being generated can be shorter than the
          // language on screen, and must not be left with zero chapters just
          // because the selected language had plenty.
          if (selectedIndex >= chapters.length || chapters.length <= 1) return t;
          return { ...t, chapters: chapters.filter((_, i) => i !== selectedIndex) };
        }),
      }));
    } else {
      toast.error("A summary must keep at least one chapter.");
    }
  };

  const handleTextEditorChange = (field, value, language) => {
    setValues((prevValues) => ({
      ...prevValues,
      translations: prevValues.translations.map((lang) =>
        lang.langCode === language
          ? { ...lang, [field]: value }
          : lang
      ),
    }));
  };

  const handleEditChapterValues = (field, value, index, language) => {
    setValues((prevValues) => ({
      ...prevValues,
      translations: prevValues.translations.map((lang) =>
        lang.langCode === language
          ? {
            ...lang,
            chapters: lang.chapters.map((chapter, idx) =>
              idx === index
                ? {
                  ...chapter,
                  [field]: field === "content" ? normalizeChapterContent(value) : value,
                }
                : chapter
            ),
          }
          : lang
      ),
    }));
  };

  const handleSelectLanguage = (language) => {
    setSelectedLang(language);

    setValues((prevValues) => {
      const exists = prevValues.translations.some((t) => t.langCode === language);
      if (exists) {
        return { ...prevValues };
      }

      return {
        ...prevValues,
        translations: [...prevValues.translations, createInitialLangValues(language)],
      };
    });
  };

  const handleGetTranslationByLang = (langCode) => {
    return values.translations?.find((lang) => lang.langCode === langCode) || {};
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmitting && !isExecutingRef.current) {
      isExecutingRef.current = true;
      callback(setIsLoading, setIsSubmit, values);
    }
  }, [errors, isSubmitting, callback, setIsLoading, setIsSubmit, values]);

  useEffect(() => {
    if (!isSubmitting) {
      isExecutingRef.current = false;
    }
  }, [isSubmitting]);

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    // A click is a fresh attempt, so clear the in-flight guard.
    //
    // `isSubmitting` is only ever set true — nothing sets it back — so the
    // effect that clears `isExecutingRef` runs once and never again. After a
    // save that failed (a server error, a dropped connection) the guard stayed
    // set, and every later click did nothing at all: no request, no error, a
    // dead button until the page was reloaded.
    isExecutingRef.current = false;

    const nextErrors = validate(values, selectedLang) ?? {};
    setErrors(nextErrors);
    setIsSubmitting(true);

    // Say something when validation fails. The form is long enough that the
    // offending field is usually scrolled out of view, so from the bottom of
    // the page the button simply looked dead — nothing moved, nothing spoke.
    const fields = Object.keys(nextErrors).filter((key) => key !== "message");
    if (fields.length > 0) {
      toast.error(
        fields.length === 1
          ? nextErrors[fields[0]]
          : `${fields.length} fields need attention before this can be saved.`
      );
      // And take them to the first one rather than making them hunt.
      requestAnimationFrame(() => {
        document
          .querySelector(".error-message")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  };

  const editorContents = {
    height: 250,
    menubar: false,
    skin: "oxide",
    plugins: [
      "advlist",
      "autolink",
      "lists",
      "link",
      "image",
      "charmap",
      "preview",
      "anchor",
      "searchreplace",
      "visualblocks",
      "code",
      "fullscreen",
      "insertdatetime",
      "media",
      "table",
      "wordcount",
    ],
    toolbar:
      "undo redo | blocks | " +
      "bold italic forecolor | alignleft aligncenter " +
      "alignright alignjustify | bullist numlist outdent indent |",
    content_style: `
        body {
          font-family: Helvetica, Arial, sans-serif;
          font-size: 14px;
          text-rendering: optimizeLegibility;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
      `,
    branding: false,
    statusbar: false,
    forced_root_block: false,
  };

  return {
    inputRef,
    generatingRef,

    values,
    setValues,
    setIsSubmit,
    errors,
    selectedLang,
    searchBookName,
    setSearchBookName,
    relatedBooksCount,
    booksData,
    summarizedBooks,
    bookSummaryId,
    editMediaError,
    initialLangRef,
    recommendeBooks,
    recommenderNames,
    isConfirmModalVisible,
    setIsConfirmModalVisible,
    isSubmit,
    isSubmitSummary,
    isLoading,
    setIsLoading,

    handleSelectLanguage,
    handleGetTranslationByLang,
    handleCreateAnIndexInChapters,
    handleRemoveAnIndexInChapters,
    handleUploadAudioFileInChapter,
    handleUploadAudioAndVideo,
    handleSearchTextChange,
    handleTextEditorChange,
    handleEditChapterValues,
    handleGetParticularBookDetail,
    handleAutoGenerateChapters,
    handleGenerateOverview,
    isGeneratingOverview,
    handleAddValues,
    handleUpdateTranslationField,
    handleUploadMedia,
    handleRemoveCoverImage,
    handleSubmit,

    editorContents,
  };
};

export default useForm;
